import { stores } from 'state/stores';
import { SupersetGroups, ExerciseSummaryExercise } from './types';
import { t } from 'translations/t';
import { formatNumberWithUnit } from 'translations/formatters';

interface SupersetGroupsExercise {
  exercise_template_id: string;
  title: string;
  thumbnail_url?: string | null;
  notes?: string | null;
  sets: any[];
  rest_seconds: number | null;
  superset_id: number | null;
}

interface ExercisesToSupersetGroupsProp {
  exercises: SupersetGroupsExercise[];
}
export const exercisesToSupersetGroups = ({
  exercises,
}: ExercisesToSupersetGroupsProp): SupersetGroups => {
  const exerciseToExerciseSummary = (e: SupersetGroupsExercise): ExerciseSummaryExercise => ({
    title: stores.exerciseTemplates.getExerciseTitle(e.exercise_template_id) || e.title,
    image: e.thumbnail_url ?? undefined,
    notes: e.notes ?? undefined,
    sets: getSets(e),
    timer: getTimer(e),
    reps: getReps(e),
  });

  return exercises.reduce((accu, e, index) => {
    if (e.superset_id === null) {
      return [...accu, { exercises: [exerciseToExerciseSummary(e)] }];
    }

    const previousExericse = exercises[index - 1];

    if (!previousExericse || previousExericse.superset_id !== e.superset_id) {
      return [...accu, { supersetId: e.superset_id, exercises: [exerciseToExerciseSummary(e)] }];
    }

    // Add the e to the previous superset group
    accu[accu.length - 1].exercises.push(exerciseToExerciseSummary(e));

    return accu;
  }, [] as SupersetGroups);
};

const getSets = (exercise: { sets: any[] }): string | undefined => {
  if (exercise.sets.length === 0) {
    return undefined;
  }
  if (exercise.sets.length === 1) {
    return t('global.numSets.singular');
  }
  if (exercise.sets.length > 1) {
    return t('global.numSets.plural', { numSets: exercise.sets.length });
  }
};

export const getReps = (exercise: { sets: any[] }): string | undefined => {
  if (exercise.sets.length === 0) return undefined;

  const min = exercise.sets
    .map(set => set.rep_range?.start || set.rep_range?.end || set.reps || 0)
    .filter(value => value !== 0)
    .sort((a, b) => a - b)[0] as number | undefined;

  const max = exercise.sets
    .map(set => set.rep_range?.end || set.rep_range?.start || set.reps || 0)
    .filter(value => value !== 0)
    .sort((a, b) => b - a)[0] as number | undefined;

  if (!min || !max) return undefined; // No reps in any set

  if (min === max) {
    if (min === 1) {
      return t('global.numReps.singular');
    }

    return t('global.numReps', { numReps: max });
  }

  return t('global.numReps.range', { min, max });
};

const getTimer = (exercise: { rest_seconds: number | null }): string | undefined => {
  if (exercise.rest_seconds === null || exercise.rest_seconds === 0) {
    return undefined;
  }

  const localizedRestTime = formatNumberWithUnit(exercise.rest_seconds, 'seconds');
  return t('global.restTime', { time: localizedRestTime });
};
